// Skill amplification coverage (Config > Skill Amplification). The backend
// (cmd/dilmeterapi/skillAmp.go) counts, per configured skill, the hits that
// had each amplifying status up; this turns those counts into the
// percentages shown on the Analysis skill rows.
import { condNameMap, skillAmpConfig, SkillAmpStatus } from '@/store';

/** Backend SkillAmpStats; Status keys are "<id>" (attacker) or "t<id>" (target). */
export type SkillAmpStats = { hits: number; all: number; any: number; status: Record<string, number> };

export type AmpCoverage = {
    hits: number;
    statuses: { key: string; id: number; name: string; onTarget: boolean; count: number; percent: number }[];
    /** Every status up at once / at least one up; only with 2+ statuses. */
    all?: { count: number; percent: number };
    any?: { count: number; percent: number };
};

const statusKey = (s: SkillAmpStatus) => (s.onTarget ? `t${s.id}` : `${s.id}`);

/** The skill's amplifying statuses, each once, in config order (as the backend merges them). */
export function ampStatusesFor(skillId: number): SkillAmpStatus[] {
    const out: SkillAmpStatus[] = [];
    for (const e of skillAmpConfig.value) {
        if (!e.skills.includes(skillId)) continue;
        for (const s of e.statuses) {
            if (!out.some(o => o.id === s.id && o.onTarget === s.onTarget)) out.push(s);
        }
    }
    return out;
}

/** mergeAmp adds b into a copy of a (targets merged on the Analysis tab). */
export function mergeAmp(a: SkillAmpStats | undefined, b: SkillAmpStats | undefined): SkillAmpStats | undefined {
    if (!a && !b) return undefined;
    const out: SkillAmpStats = { hits: 0, all: 0, any: 0, status: {} };
    for (const x of [a, b]) {
        if (!x) continue;
        out.hits += x.hits;
        out.all += x.all;
        out.any += x.any;
        for (const [k, v] of Object.entries(x.status || {})) out.status[k] = (out.status[k] ?? 0) + v;
    }
    return out;
}

/** ampCoverage is a skill's coverage for display, or undefined when it isn't configured. */
export function ampCoverage(skillId: number, amp: SkillAmpStats | undefined): AmpCoverage | undefined {
    const statuses = ampStatusesFor(skillId);
    if (!statuses.length || !amp || !(amp.hits > 0)) return undefined;
    const pct = (n: number) => (n / amp.hits) * 100;
    const cov: AmpCoverage = {
        hits: amp.hits,
        statuses: statuses.map(s => {
            const count = amp.status?.[statusKey(s)] ?? 0;
            return {
                key: statusKey(s), id: s.id, onTarget: s.onTarget, count, percent: pct(count),
                name: condNameMap.value?.[s.id] ?? `Status ${s.id}`,
            };
        }),
    };
    if (statuses.length > 1) {
        cov.all = { count: amp.all, percent: pct(amp.all) };
        cov.any = { count: amp.any, percent: pct(amp.any) };
    }
    return cov;
}
